import React, { useState } from 'react';
import { ChevronDown, ChevronRight } from 'lucide-react';

function formatTime(ts) {
  if (!ts) return '';
  const d = new Date(ts);
  const hh = String(d.getHours()).padStart(2, '0');
  const mm = String(d.getMinutes()).padStart(2, '0');
  const ss = String(d.getSeconds()).padStart(2, '0');
  const millis = String(d.getMilliseconds()).padStart(3, '0');
  return `${hh}:${mm}:${ss}.${millis}`;
}

function maskValue(key, val) {
  const sensitive = ['authorization', 'cookie', 'set-cookie', 'x-api-key', 'api-key', 'token', 'secret'];
  if (sensitive.some((s) => key.toLowerCase().includes(s))) return '••••••••••';
  return String(val);
}

function statusCls(status) {
  if (status == null) return '';
  if (status >= 500) return 'req-val--err';
  if (status >= 400) return 'req-val--warn';
  if (status >= 300) return 'req-val--redir';
  return '';
}

function Section({ title, defaultOpen = false, children }) {
  const [open, setOpen] = useState(defaultOpen);
  return (
    <div className="req-section">
      <button type="button" className="req-section__head" onClick={() => setOpen((v) => !v)}>
        {open ? <ChevronDown size={12} /> : <ChevronRight size={12} />}
        {title}
      </button>
      {open && <div className="req-section__body">{children}</div>}
    </div>
  );
}

function BodyBlock({ body }) {
  if (body == null || body === '') return <span className="req-empty">Empty</span>;
  const text = typeof body === 'string' ? body : JSON.stringify(body, null, 2);
  return <pre className="req-body-pre">{text}</pre>;
}

export default function ConsoleEntryDetail({ entry }) {
  if (!entry) {
    return <div className="req-detail req-detail--empty panel-scroll">Select a request to inspect</div>;
  }

  const reqHeaders = Object.entries(entry.requestHeaders || {});
  const resHeaders = Object.entries(entry.responseHeaders || {});

  return (
    <div className="req-detail panel-scroll">
      <div className="req-detail__header">
        <span className="req-detail__title">Request</span>
        <span className="req-detail__ts">{formatTime(entry.timestamp)}</span>
      </div>

      <div className="req-detail__meta">
        <div className="req-meta-row">
          <span className="req-meta-row__key">URL</span>
          <span className="req-meta-row__val req-meta-row__val--mono">{entry.url}</span>
        </div>
        <div className="req-meta-row">
          <span className="req-meta-row__key">Method</span>
          <span className="req-meta-row__val req-meta-row__val--bold">{entry.method}</span>
        </div>
        <div className="req-meta-row">
          <span className="req-meta-row__key">Status</span>
          <span className={`req-meta-row__val req-meta-row__val--bold ${statusCls(entry.status)}`}>
            {entry.status != null ? entry.status : 'Failed'}
          </span>
        </div>
        <div className="req-meta-row">
          <span className="req-meta-row__key">Duration</span>
          <span className="req-meta-row__val req-meta-row__val--bold">
            {entry.durationMs != null ? `${entry.durationMs}ms` : '—'}
          </span>
        </div>
        <div className="req-meta-row">
          <span className="req-meta-row__key">Size</span>
          <span className="req-meta-row__val req-meta-row__val--bold">
            {entry.responseSizeBytes != null
              ? entry.responseSizeBytes === 0
                ? '0 B'
                : `${(entry.responseSizeBytes / 1024).toFixed(1)} KB`
              : '—'}
          </span>
        </div>
        {entry.error && (
          <div className="req-meta-row">
            <span className="req-meta-row__key">Error</span>
            <span className="req-meta-row__val req-val--err">{entry.error}</span>
          </div>
        )}
      </div>

      {reqHeaders.length > 0 && (
        <div className="req-detail__headers-wrap">
          <div className="req-section-label">HEADERS</div>
          <div className="req-headers">
            {reqHeaders.map(([k, v]) => (
              <div key={k} className="req-header-row">
                <span className="req-header-row__key">{k}</span>
                <span className="req-header-row__val">{maskValue(k, v)}</span>
              </div>
            ))}
          </div>
        </div>
      )}

      {resHeaders.length > 0 && (
        <Section title="Response Headers">
          <div className="req-headers">
            {resHeaders.map(([k, v]) => (
              <div key={k} className="req-header-row">
                <span className="req-header-row__key">{k}</span>
                <span className="req-header-row__val">{maskValue(k, v)}</span>
              </div>
            ))}
          </div>
        </Section>
      )}

      {entry.requestBody != null && entry.requestBody !== '' && (
        <Section title="Request Body">
          <BodyBlock body={entry.requestBody} />
        </Section>
      )}

      {entry.responseBody != null && entry.responseBody !== '' && (
        <Section title="Response Body" defaultOpen>
          <BodyBlock body={entry.responseBody} />
        </Section>
      )}
    </div>
  );
}
